/**
 * Server-Side Cache Service
 * 
 * Saves and loads cache data from static JSON files to persist data
 * across site reloads without requiring SharePoint synchronization.
 */

import { publicCacheService, type PublicClass, type PublicKit, type PublicGalleryItem, type PublicImage } from './publicCacheService'

interface CacheData {
  classes: PublicClass[]
  kits: PublicKit[]
  gallery: PublicGalleryItem[]
  images: PublicImage[]
  lastUpdated: string
}

class ServerCacheService {
  private readonly CACHE_FILE_PATH = '/api/cache.json'
  private isInitialized = false

  /**
   * Initialize server cache - load from server and populate local storage
   */
  async initialize(): Promise<boolean> {
    if (this.isInitialized) {
      return true
    }

    console.log('ServerCache: Initializing from server cache...')
    
    try {
      // Try to load cache from server
      const cacheData = await this.loadFromServer()
      
      if (cacheData) {
        // Populate local storage with server cache data
        await this.populateLocalStorage(cacheData)
        console.log('ServerCache: Successfully loaded from server cache')
        this.isInitialized = true
        return true
      } else {
        console.log('ServerCache: No server cache found, will need manual sync')
        return false
      }
    } catch (error) {
      console.error('ServerCache: Failed to initialize from server cache:', error)
      return false
    }
  }

  /**
   * Save current cache data to server (called from admin panel)
   */
  async saveToServer(): Promise<boolean> {
    try {
      console.log('ServerCache: Saving current cache to server...')
      
      // Collect all current cache data
      const cacheData: CacheData = {
        classes: publicCacheService.getClasses(),
        kits: publicCacheService.getKits(),
        gallery: await publicCacheService.getGalleryItems(),
        images: publicCacheService.getImages(),
        lastUpdated: new Date().toISOString()
      }

      // Save to static file (in production this would need a build process)
      await this.saveStaticCacheFile(cacheData)
      
      console.log('ServerCache: Successfully saved cache to server')
      return true
    } catch (error) {
      console.error('ServerCache: Failed to save cache to server:', error)
      return false
    }
  }

  /**
   * Load cache data from server
   */
  private async loadFromServer(): Promise<CacheData | null> {
    try {
      const response = await fetch(this.CACHE_FILE_PATH, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      })
      
      if (!response.ok) {
        if (response.status === 404) {
          console.log('ServerCache: No cache file found on server')
          return null
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const cacheData: CacheData = await response.json()
      console.log(`ServerCache: Loaded cache from server (last updated: ${cacheData.lastUpdated})`)
      return cacheData
    } catch (error) {
      console.error('ServerCache: Error loading from server:', error)
      return null
    }
  }

  /**
   * Populate local storage with server cache data
   */
  private async populateLocalStorage(cacheData: CacheData): Promise<void> {
    try {
      // Store in admin cache format (for compatibility)
      localStorage.setItem('salty_mermaid_classes', JSON.stringify(cacheData.classes))
      localStorage.setItem('salty_mermaid_kits', JSON.stringify(cacheData.kits.map(kit => ({
        ...kit,
        name: kit.name,
        stockQuantity: kit.stockCount,
        features: kit.materials,
        imageId: kit.imageId
      }))))
      localStorage.setItem('salty_mermaid_gallery', JSON.stringify(cacheData.gallery))
      localStorage.setItem('salty_mermaid_images', JSON.stringify(cacheData.images.map(img => ({
        id: img.id,
        name: img.filename,
        dataUrl: img.dataUrl,
        category: img.category,
        uploadedAt: img.uploadDate
      }))))

      // Update public cache
      publicCacheService.updateClassesCache(cacheData.classes)
      publicCacheService.updateKitsCache(cacheData.kits)
      publicCacheService.updateGalleryCache(cacheData.gallery)
      publicCacheService.updateImagesCache(cacheData.images)

      console.log('ServerCache: Populated local storage with server cache data')
    } catch (error) {
      console.error('ServerCache: Error populating local storage:', error)
      throw error
    }
  }

  /**
   * Save cache data as static file (for Netlify deployment)
   */
  private async saveStaticCacheFile(cacheData: CacheData): Promise<void> {
    // In a static site, we need to save this to the public folder
    // and trigger a rebuild. For now, we'll use a simple approach
    // where the admin can download the cache file and manually upload it.
    
    const cacheBlob = new Blob([JSON.stringify(cacheData, null, 2)], { 
      type: 'application/json' 
    })
    
    const url = URL.createObjectURL(cacheBlob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'cache.json'
    link.textContent = 'Download Cache File'
    
    // Auto-download the cache file
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    
    console.log('ServerCache: Cache file downloaded - upload to /public/api/ folder and redeploy')
  }

  /**
   * Get cache status information
   */
  getStatus(): { isInitialized: boolean; hasData: boolean } {
    return {
      isInitialized: this.isInitialized,
      hasData: this.isInitialized && (
        publicCacheService.getClasses().length > 0 ||
        publicCacheService.getKits().length > 0 ||
        publicCacheService.getImages().length > 0
      )
    }
  }

  /**
   * Force reload from server (useful for admin)
   */
  async forceReload(): Promise<boolean> {
    this.isInitialized = false
    return await this.initialize()
  }
}

export const serverCacheService = new ServerCacheService()